import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaUsers, FaCrown, FaEnvelope, FaShoppingBag, FaWallet, FaUserFriends, FaChartLine, FaStore } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import './CustomerList.css';

// Dummy data for "experience" role
const dummyCustomerListData = [
  { id: 'exp_cust_1', first_name: 'Alice', last_name: 'Experience', email: 'alice.exp@example.com', orders_count: 15, total_spent: '1250.75', currency: 'EUR' },
  { id: 'exp_cust_2', first_name: 'Bob', last_name: 'Demo', email: 'bob.demo@example.com', orders_count: 5, total_spent: '340.50', currency: 'EUR' },
  { id: 'exp_cust_3', first_name: 'Carol', last_name: 'Tester', email: 'carol.test@example.com', orders_count: 22, total_spent: '2100.00', currency: 'EUR' }, // Top customer
  { id: 'exp_cust_4', first_name: 'David', last_name: 'Sample', email: 'david.sample@example.com', orders_count: 1, total_spent: '45.99', currency: 'EUR' },
  { id: 'exp_cust_5', first_name: 'Eve', last_name: 'User', email: 'eve.user@example.com', orders_count: 8, total_spent: '680.20', currency: 'EUR' },
];

const CustomerList = () => {
  const [customers, setCustomers] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [shopStatus, setShopStatus] = useState('configured');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCustomers = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const token = sessionStorage.getItem('access_token');
        const userDataString = sessionStorage.getItem('userData');
        let userRole = null;

        if (!token) {
          navigate('/');
          setIsLoading(false);
          return;
        }
        
        if (userDataString) {
          try {
            userRole = JSON.parse(userDataString).role;
          } catch (parseError) {
            console.error("Failed to parse user data from sessionStorage:", parseError);
          }
        }

        if (userRole === 'experience') {
          console.log("Experience user detected, loading dummy customer list.");
          setCustomers(dummyCustomerListData);
          setShopStatus('configured'); // Assume shop is configured for experience user display
          setIsLoading(false);
          return; // Stop further execution for experience user
        }

        // Real API call for other users
        const response = await axios.get('/api/shopify/get_customers', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCustomers(response.data.customers || []);
        if (response.data.status === 'unconfigured') {
          setShopStatus('unconfigured');
        } else {
          setShopStatus('configured'); // Explicitly set if not unconfigured
        }
      } catch (err) {
        console.error("Error fetching customers:", err);
        setError(err.message);
        if (err.response && (err.response.status === 401 || err.response.status === 422)) {
          sessionStorage.removeItem('access_token');
          sessionStorage.removeItem('userData');
          navigate('/');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchCustomers();
  }, [navigate]); // Added navigate to dependency array

  const handleCustomerClick = (customerId) => {
    navigate(`/customers/${customerId}`);
  };

  // Sort customers by total spent to identify the top customer
  const sortedCustomers = [...customers].sort((a, b) => 
    parseFloat(b.total_spent) - parseFloat(a.total_spent)
  );

  if (error) return <div className="una-customer-error">Error: {error}</div>;

  if (isLoading) {
    return (
      <div className="una-dashboard-loading-screen">
        <div className="una-loading-content">
          <div className="una-loading-logo">
            <img src="/bgless_logo.png" alt="Unanimity AI Logo" />
          </div>
          <h2>Loading Customers</h2>
          <div className="una-loading-progress">
            <div className="una-progress-bar">
              <div className="una-progress-glow"></div>
            </div>
          </div>
          <div className="una-loading-metrics">
            <div className="una-metric-item">
              <FaUsers />
              <span>Fetching Customer Data</span>
            </div>
            <div className="una-metric-item">
              <FaShoppingBag />
              <span>Processing Orders</span>
            </div>
            <div className="una-metric-item">
              <FaStore />
              <span>Loading Store Data</span>
            </div>
            <div className="una-metric-item">
              <FaWallet />
              <span>Calculating Totals</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="una-customer-container">
      <div className="una-customer-header">
        <div className="una-customer-title">
          <FaUsers />
          <h2>Customers</h2>
        </div>
      </div>

      {isLoading ? (
        <div className="una-customers-loading-state">
          <div className="loading-spinner"></div>
          <p>Loading customers...</p>
        </div>
      ) : shopStatus === 'unconfigured' ? (
        <div className="una-customers-empty-state">
          <FaStore className="empty-icon" />
          <p>Please complete your shop configuration to view customers</p>
          <button 
            className="una-configure-shop-button"
            onClick={() => navigate('/settings')}
          >
            Configure Shop
          </button>
        </div>
      ) : !customers.length ? (
        <div className="una-customers-empty-state">
          <FaUsers className="empty-icon" />
          <p>No customers found</p>
        </div>
      ) : (
        <div className="una-customer-grid">
          {sortedCustomers.map((customer, index) => (
            <div 
              key={customer.id} 
              className={`una-customer-card ${index === 0 ? 'una-customer-card-top' : ''}`}
              onClick={() => handleCustomerClick(customer.id)}
            >
              {index === 0 && (
                <div className="una-customer-crown">
                  <FaCrown />
                </div>
              )}
              
              <div className="una-customer-card-header">
                <h3>{`${customer.first_name} ${customer.last_name}`}</h3>
              </div>

              <div className="una-customer-card-content">
                <div className="una-customer-info">
                  <FaEnvelope />
                  <span>{customer.email}</span>
                </div>

                <div className="una-customer-info">
                  <FaShoppingBag />
                  <span>{customer.orders_count} orders</span>
                </div>

                <div className="una-customer-info">
                  <FaWallet />
                  <span>{customer.total_spent} {customer.currency}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomerList;